import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface SeoData {
  title: string;
  description: string;
  /** Path with leading slash, e.g. '/suche'. Becomes canonical + og:url. */
  path: string;
  /** Optional absolute URL for og:image. Defaults to /og-default.png on the site origin. */
  image?: string;
  /** og:type — 'website' for landing/index pages, 'profile' for handwerker pages, 'article' for legal/blog. */
  type?: 'website' | 'profile' | 'article';
  /** When true, sets robots = noindex,follow. Use for auth/internal pages. */
  noindex?: boolean;
}

const SITE_URL = 'https://get-craftly.de';
const SITE_NAME = 'Craftly';
const DEFAULT_IMAGE = `${SITE_URL}/og-default.png`;

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject(DOCUMENT);

  apply(data: SeoData): void {
    const url = this.absoluteUrl(data.path);
    const image = data.image ?? DEFAULT_IMAGE;
    const type = data.type ?? 'website';

    this.title.setTitle(data.title);

    this.upsertName('description', data.description);
    this.upsertName('robots', data.noindex ? 'noindex,follow' : 'index,follow');

    this.upsertProperty('og:title', data.title);
    this.upsertProperty('og:description', data.description);
    this.upsertProperty('og:url', url);
    this.upsertProperty('og:type', type);
    this.upsertProperty('og:image', image);
    this.upsertProperty('og:site_name', SITE_NAME);
    this.upsertProperty('og:locale', 'de_DE');

    this.upsertName('twitter:card', 'summary_large_image');
    this.upsertName('twitter:title', data.title);
    this.upsertName('twitter:description', data.description);
    this.upsertName('twitter:image', image);

    this.setCanonical(url);
  }

  /** Set or replace a JSON-LD <script type="application/ld+json"> with given id. SSR-safe. */
  setJsonLd(id: string, data: object): void {
    const head = this.doc.head;
    if (!head) return;
    let script = this.doc.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = this.doc.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }

  private setCanonical(url: string): void {
    const head = this.doc.head;
    if (!head) return;
    let link = head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private upsertName(name: string, content: string): void {
    if (this.meta.getTag(`name="${name}"`)) {
      this.meta.updateTag({ name, content });
    } else {
      this.meta.addTag({ name, content });
    }
  }

  private upsertProperty(property: string, content: string): void {
    if (this.meta.getTag(`property="${property}"`)) {
      this.meta.updateTag({ property, content });
    } else {
      this.meta.addTag({ property, content });
    }
  }

  private absoluteUrl(path: string): string {
    const clean = path.startsWith('/') ? path : `/${path}`;
    return `${SITE_URL}${clean}`;
  }
}
